// Paramètres › « Coupons » (2026-10-28): the 3 switches (each saved on tap),
// how the printer turns the sheet for the backs, the backs' offset in mm, and
// the 1-sheet alignment test. All three switches OFF = nothing changes anywhere.
import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { settingsApi, type AppSettings } from '@/lib/api';
import { Button } from '@/components/ui/Button';
import { useT } from '@/lib/i18n';
import { Toggle, inputCls } from '../settings/AiProvidersSections';
import { alignmentPdfAvailable, downloadAlignmentPdf } from './alignmentPdf';

type SwitchKey = 'coupons_enabled' | 'coupon_redeem_enabled' | 'editor_outro_page_enabled';

const SWITCHES: Array<{ key: SwitchKey; label: string; hint: string }> = [
  { key: 'coupons_enabled', label: 'Imprimer les coupons dans les albums', hint: 'Les pages libres en fin d’album deviennent une feuille de 4 coupons (ou un PDF coupon à part).' },
  { key: 'coupon_redeem_enabled', label: 'Accepter les codes sur le site', hint: 'Champ « Code promo » au paiement, et bouton « Appliquer un coupon » sur la page commande.' },
  { key: 'editor_outro_page_enabled', label: 'Page de fin dans l’éditeur', hint: 'Le « + » de l’éditeur propose aussi « Page de fin ».' },
];

export function CouponsSettingsSection({ settings }: { settings: AppSettings | undefined }) {
  const { t } = useT();
  const qc = useQueryClient();
  // Local edits over the saved values (null = show what is saved).
  const [edit, setEdit] = useState<{ flip?: 'long' | 'short'; x?: number; y?: number }>({});
  const flip = edit.flip ?? settings?.coupon_duplex_flip ?? 'long';
  const offX = edit.x ?? Number(settings?.coupon_offset_x_mm ?? 0);
  const offY = edit.y ?? Number(settings?.coupon_offset_y_mm ?? 0);
  const setFlip = (v: 'long' | 'short') => setEdit((p) => ({ ...p, flip: v }));
  const setOffX = (v: number) => setEdit((p) => ({ ...p, x: v }));
  const setOffY = (v: number) => setEdit((p) => ({ ...p, y: v }));
  const [saved, setSaved] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const [testErr, setTestErr] = useState<string | null>(null);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ['settings'] });
    qc.invalidateQueries({ queryKey: ['app-settings'] });
  };

  const sw = useMutation({
    mutationFn: (p: Partial<AppSettings>) => settingsApi.update(p),
    onSuccess: refresh,
  });

  const print = useMutation({
    mutationFn: () => settingsApi.update({ coupon_duplex_flip: flip, coupon_offset_x_mm: offX, coupon_offset_y_mm: offY }),
    onSuccess: () => { refresh(); setEdit({}); setSaved(true); setTimeout(() => setSaved(false), 2000); },
  });

  const printDirty = !!settings && (
    flip !== (settings.coupon_duplex_flip ?? 'long')
    || offX !== Number(settings.coupon_offset_x_mm ?? 0)
    || offY !== Number(settings.coupon_offset_y_mm ?? 0));

  async function runTest() {
    if (!settings) return;
    setTestBusy(true);
    setTestErr(null);
    try {
      // The sheet is drawn with what is on screen, saved or not — try, then save.
      await downloadAlignmentPdf({ ...settings, coupon_duplex_flip: flip, coupon_offset_x_mm: offX, coupon_offset_y_mm: offY });
    } catch (e) {
      setTestErr(e instanceof Error ? e.message : String(e));
    }
    setTestBusy(false);
  }

  return (
    <div className="bg-white rounded-xl border border-gray-200 p-5">
      <div className="mb-4">
        <h2 className="text-sm font-semibold text-gray-900">{t('Coupons')}</h2>
        <p className="text-xs text-gray-500 mt-0.5">
          {t('Tout est éteint par défaut. Préparez campagnes et designs dans')}{' '}
          <Link to="/admin/coupons" className="font-semibold text-adm-gold-ink hover:underline">{t('Coupons')}</Link>
          {t(', puis allumez ici.')}
        </p>
      </div>

      <div className="space-y-3">
        {SWITCHES.map((s) => (
          <div key={s.key} className="flex items-start justify-between gap-3 rounded-lg border border-adm-line p-3">
            <div className="min-w-0">
              <p className="text-sm font-semibold text-adm-ink">{t(s.label)}</p>
              <p className="text-[11px] text-adm-muted mt-0.5">{t(s.hint)}</p>
            </div>
            <Toggle on={!!settings?.[s.key]} label={t(s.label)} disabled={!settings || sw.isPending}
              onChange={(v) => sw.mutate({ [s.key]: v } as Partial<AppSettings>)} />
          </div>
        ))}
        {sw.isError && <p className="text-xs text-status-stuck">{(sw.error as Error).message}</p>}
      </div>

      <div className="mt-6 space-y-3">
        <p className="text-[13px] font-semibold text-adm-ink">{t('Impression recto-verso des coupons')}</p>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2" role="radiogroup" aria-label={t('Retournement de la feuille')}>
          {([
            ['long', 'Bord long', 'La feuille tourne comme une page de livre (réglage habituel).'],
            ['short', 'Bord court', 'La feuille tourne de haut en bas, comme un bloc-notes.'],
          ] as const).map(([v, title, desc]) => (
            <label key={v} className={`flex items-start gap-2 rounded-lg border p-3 cursor-pointer ${flip === v ? 'border-adm-gold bg-adm-gold-wash' : 'border-adm-line'}`}>
              <input type="radio" name="coupon-flip" className="mt-1 accent-[#C9A24A]" checked={flip === v} onChange={() => setFlip(v)} />
              <span>
                <span className="block text-sm font-semibold text-adm-ink">{t(title)}</span>
                <span className="block text-[11px] text-adm-muted">{t(desc)}</span>
              </span>
            </label>
          ))}
        </div>
        <div className="grid grid-cols-2 gap-3 sm:max-w-sm">
          <label className="block">
            <span className="block text-xs font-semibold text-adm-ink-2 mb-1">{t('Décalage X (mm)')}</span>
            <input type="number" step={0.1} className={inputCls} value={offX} onChange={(e) => setOffX(Number(e.target.value) || 0)} />
          </label>
          <label className="block">
            <span className="block text-xs font-semibold text-adm-ink-2 mb-1">{t('Décalage Y (mm)')}</span>
            <input type="number" step={0.1} className={inputCls} value={offY} onChange={(e) => setOffY(Number(e.target.value) || 0)} />
          </label>
        </div>
        <p className="text-[11px] text-adm-muted">
          {t('Si le verso tombe à côté du recto : imprimez la feuille de test, mesurez l’écart, entrez-le ici (X = vers la droite, Y = vers le bas ; négatif = l’inverse).')}
        </p>
        <div className="flex flex-wrap items-center gap-2">
          <Button size="sm" onClick={() => print.mutate()} loading={print.isPending} disabled={!printDirty}>{t('Enregistrer')}</Button>
          <Button size="sm" variant="secondary" onClick={() => void runTest()} loading={testBusy} disabled={!settings || !alignmentPdfAvailable()}>
            {t('Télécharger la feuille de test d’alignement')}
          </Button>
          {saved && <span className="text-xs font-semibold text-status-done">✓ {t('Enregistré')}</span>}
        </div>
        {!alignmentPdfAvailable() && <p className="text-[11px] text-adm-muted">{t('La feuille de test arrive avec la mise à jour de l’impression des coupons.')}</p>}
        {print.isError && <p className="text-xs text-status-stuck">{(print.error as Error).message}</p>}
        {testErr && <p className="text-xs text-status-stuck">{testErr}</p>}
      </div>
    </div>
  );
}
